import { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Cookie, X } from "lucide-react";

const COOKIE_KEY = "cky-consent";

const ADMIN_PATHS = [
  "/dashboard", "/clinicas", "/painel-clinica", "/orcamentos", "/leads",
  "/internacoes", "/cupons", "/mapa-familia", "/mapa-atendentes", "/pesquisa",
  "/ambulancias", "/fichas", "/visitas", "/lembretes", "/documentos",
  "/sugestoes", "/notificacoes", "/perfil", "/usuarios", "/seo", "/anuncios",
];

const isAdminRoute = (pathname: string) =>
  ADMIN_PATHS.some((p) => pathname === p || pathname.startsWith(p + "/"));

type Consent = "accepted" | "rejected" | null;

const CookieConsent = () => {
  const location = useLocation();
  const [consent, setConsent] = useState<Consent>(() => {
    try { return localStorage.getItem(COOKIE_KEY) as Consent; } catch { return null; }
  });
  const [showBanner, setShowBanner] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [hiddenRevisit, setHiddenRevisit] = useState(false);

  const onAdminRoute = isAdminRoute(location.pathname);

  useEffect(() => {
    if (!consent) {
      const t = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(t);
    }
  }, [consent]);

  const accept = () => { localStorage.setItem(COOKIE_KEY, "accepted"); setConsent("accepted"); setShowBanner(false); };
  const reject = () => { localStorage.setItem(COOKIE_KEY, "rejected"); setConsent("rejected"); setShowBanner(false); };
  const revisit = () => setShowBanner(true);

  if (onAdminRoute) return null;

  return (
    <>
      {/* Revisit button */}
      {!showBanner && consent && !hiddenRevisit && (
        <div className="fixed bottom-4 left-4 z-[9999] group">
          <button
            onClick={revisit}
            aria-label="Configurações de Cookies"
            title="Configurações de Cookies"
            className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-transform hover:scale-110"
            style={{ backgroundColor: "#ff5254" }}
          >
            <Cookie className="h-6 w-6 text-white" />
          </button>
          <button
            onClick={() => setHiddenRevisit(true)}
            aria-label="Fechar"
            className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-muted border border-border shadow flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive hover:text-destructive-foreground"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      {/* Consent banner */}
      {showBanner && (
        <div className="fixed bottom-4 left-4 z-[9999] max-w-sm w-[calc(100%-2rem)] sm:w-96 animate-in slide-in-from-bottom-4 fade-in duration-300">
          <div className="rounded-xl border bg-white dark:bg-zinc-900 shadow-2xl p-5 space-y-3">
            <h2 className="font-semibold text-sm text-zinc-900 dark:text-zinc-100">
              Nós valorizamos sua privacidade
            </h2>
            <p className="text-xs text-zinc-600 dark:text-zinc-400 leading-relaxed">
              Nós utilizamos cookies para melhorar sua experiência de navegação, mostrar anúncios
              personalizados ou conteúdo, e analisar nosso tráfego. Clicando em "Aceitar todos",
              você consente com nosso uso de cookies. Leia nossa{" "}
              <Link to="/politica-de-privacidade" className="underline text-primary">Política de Privacidade</Link>.
            </p>

            {showCustomize && (
              <div className="text-xs text-zinc-500 dark:text-zinc-400 space-y-1.5 border-t pt-3">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked disabled className="accent-[#ff5254]" /> Essenciais (sempre ativo)
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" defaultChecked className="accent-[#ff5254]" /> Analytics
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" defaultChecked className="accent-[#ff5254]" /> Marketing
                </label>
              </div>
            )}

            <div className="flex flex-wrap gap-2 pt-1">
              <button
                onClick={() => setShowCustomize(!showCustomize)}
                className="px-3 py-1.5 text-xs font-medium rounded-md border transition-colors"
                style={{ color: "#991b1b", borderColor: "#991b1b" }}
              >
                Customizar
              </button>
              <button
                onClick={reject}
                className="px-3 py-1.5 text-xs font-medium rounded-md border transition-colors"
                style={{ color: "#991b1b", borderColor: "#991b1b" }}
              >
                Rejeitar todos
              </button>
              <button
                onClick={accept}
                className="px-3 py-1.5 text-xs font-medium rounded-md border text-white transition-colors"
                style={{ backgroundColor: "#991b1b", borderColor: "#991b1b" }}
              >
                Aceitar todos
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
